/**
 * Activity renderers — one component per content type (the exact census
 * of the library) + the dispatcher the player uses.
 *
 * Contract: every activity calls `onDone(correct)` exactly once when the
 * learner finishes it — `true`/`false` for SCORED types, `null` for
 * informational ones (tips, examples, dialogues; they never hurt the
 * score). Scored types show instant feedback, then a Continue button.
 */
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Button } from "../../components/ui";
import { TapToTranslate } from "../../components/TapToTranslate";
import { useI18n, useT } from "../../i18n";
import { speakBest } from "../../utils/voice";
import { voiceForSpeaker } from "./speakerVoice";
import type { GrammarTable } from "../../api/reference";
import { GrammarTableView } from "./GrammarTableView";
import { LEARN_BLOCK_RENDERERS, LEARN_SCORED_TYPES, pick } from "./learnBlocks";
import type { Localised } from "./learnBlocks";

/** Resolve an option-list answer from either `answer_index` (the v2 content
 * shape used by every German lesson) or a legacy `answer` string. Returns the
 * answer TEXT so feedback and correct-marking work regardless of shape. */
function resolveAnswer(payload: Record<string, unknown>, options: string[]): string {
  const idx = payload.answer_index;
  if (typeof idx === "number" && options[idx] != null) return options[idx];
  return String(payload.answer ?? "");
}

export type ActivityResult = boolean | null;

export interface ActivityProps {
  payload: Record<string, unknown>;
  onDone: (correct: ActivityResult) => void;
}

export const SCORED_TYPES = new Set([
  "listening",
  "multiple_choice",
  "gap_fill",
  "translate_pick",
  "sentence_builder",
  "article_swipe",
  ...LEARN_SCORED_TYPES,
]);

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
function SpeakButton({ text }: { text: string }) {
  const t = useT();
  return (
    <Button
      variant="secondary"
      onClick={() => void speakBest(text, { lang: "de-DE" })}
      aria-label={`${t("player.listen")}: ${text}`}
    >
      🔊 {t("player.listen")}
    </Button>
  );
}

/** Feedback banner + Continue, shown after a scored answer. Always reveals the
 * correct answer (so a learner who missed it actually learns it), and shows an
 * optional bilingual explanation when the content carries one. */
function Feedback({
  correct,
  answer,
  explanation,
  onContinue,
}: {
  correct: boolean;
  answer: string;
  explanation?: string;
  onContinue: () => void;
}) {
  const t = useT();
  return (
    <div
      role="status"
      className={`mt-4 rounded-card border p-4 ${correct ? "border-success/40 bg-success/5" : "border-danger/40 bg-danger/5"}`}
    >
      <p className={`font-bold ${correct ? "text-success" : "text-danger"}`}>
        {correct ? t("player.correct") : t("player.incorrect")}
      </p>
      {!correct && answer && (
        <p className="mt-1 text-sm text-ink">
          {t("player.answerWas")} <strong>{answer}</strong>
        </p>
      )}
      {explanation && (
        <p className="mt-2 border-l-2 border-brand/40 pl-3 text-sm text-ink">
          {explanation}
        </p>
      )}
      <Button onClick={onContinue} className="mt-3">
        {t("player.continue")}
      </Button>
    </div>
  );
}

/** Option-list scored activity — the shared engine behind listening,
 * multiple_choice, gap_fill and translate_pick (they differ only in the
 * prompt block above the options). */
function OptionsActivity({
  payload,
  onDone,
  prompt,
}: ActivityProps & { prompt: ReactNode }) {
  const t = useT();
  const { locale } = useI18n();
  const options = (payload.options as string[]) ?? [];
  const answer = resolveAnswer(payload, options);
  const explanation = pick(payload.explanation as Localised | string | undefined, locale);
  const [picked, setPicked] = useState<string | null>(null);

  if (options.length === 0) {
    // Malformed content (no options) — never strand the learner; skip
    // unscored so bad data can't hurt anyone's mastery.
    return (
      <div>
        {prompt}
        <Button onClick={() => onDone(null)} className="mt-4">
          {t("player.continue")}
        </Button>
      </div>
    );
  }

  return (
    <div>
      {prompt}
      <div className="mt-4 grid grid-cols-1 gap-2">
        {options.map((option) => (
          <Button
            key={option}
            variant={
              picked === null
                ? "secondary"
                : option === answer
                  ? "primary"
                  : option === picked
                    ? "danger"
                    : "secondary"
            }
            disabled={picked !== null}
            onClick={() => setPicked(option)}
            className="justify-start text-left"
          >
            {option}
          </Button>
        ))}
      </div>
      {picked !== null && (
        <Feedback
          correct={picked === answer}
          answer={answer}
          explanation={explanation}
          onContinue={() => onDone(picked === answer)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Informational types
// ---------------------------------------------------------------------------
export function GrammarTip({ payload, onDone }: ActivityProps) {
  const t = useT();
  const { locale } = useI18n();
  const L = (v: unknown) => pick(v as Localised | string | undefined, locale);
  // v2 content: title/explanation/contrast/mistake are {en,de}; examples are
  // {de, note:{en,de}}. Legacy content: title/body strings, examples {de,en}.
  const title = L(payload.title);
  const explanation = L(payload.explanation ?? payload.body);
  const contrast = L(payload.contrast);
  const mistake = L(payload.mistake);
  const examples =
    (payload.examples as { de: string; note?: Localised | string; en?: string }[]) ?? [];
  return (
    <div className="rounded-card border border-brand/30 bg-brand-soft p-4">
      <h3 className="text-lg font-bold text-ink">💡 {title}</h3>
      {explanation && (
        <p className="mt-2 whitespace-pre-line text-ink">{explanation}</p>
      )}
      {contrast && (
        <p className="mt-3 border-l-2 border-brand/40 pl-3 text-sm text-ink">
          {contrast}
        </p>
      )}
      {examples.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {examples.map((ex, i) => {
            const note = ex.note != null ? L(ex.note) : (ex.en ?? "");
            return (
              <li key={i} className="text-sm">
                <strong className="text-ink">{ex.de}</strong>
                {note && (
                  <>
                    {" — "}
                    <span className="text-muted">{note}</span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {mistake && (
        <p className="mt-3 rounded-control bg-danger/5 px-3 py-2 text-sm text-ink">
          <span className="font-semibold text-danger">⚠ </span>
          {mistake}
        </p>
      )}
      <Button onClick={() => onDone(null)} className="mt-4">
        {t("player.continue")}
      </Button>
    </div>
  );
}

export function ExampleSentence({ payload, onDone }: ActivityProps) {
  const t = useT();
  const de = String(payload.de ?? "");
  return (
    <div>
      <p className="text-xl font-bold text-ink">{de}</p>
      <p className="mt-1 text-muted">{String(payload.en ?? "")}</p>
      {typeof payload.focus === "string" && payload.focus && (
        <p className="mt-2 text-sm text-brand-dark">
          Focus: <strong>{payload.focus}</strong>
        </p>
      )}
      <div className="mt-4 flex gap-2">
        <SpeakButton text={de} />
        <Button onClick={() => onDone(null)}>{t("player.continue")}</Button>
      </div>
    </div>
  );
}

export function Dialogue({ payload, onDone }: ActivityProps) {
  const t = useT();
  const lines = (payload.lines as { speaker: string; de: string; en: string }[]) ?? [];
  return (
    <div>
      {typeof payload.title === "string" && (
        <h3 className="mb-3 text-lg font-bold text-ink">🗣 {payload.title}</h3>
      )}
      <ul className="space-y-2">
        {lines.map((line, i) => (
          <li key={i} className="rounded-control bg-canvas p-2 text-sm">
            <button
              aria-label={`speak line ${i + 1}`}
              onClick={() =>
                void speakBest(line.de, {
                  lang: "de-DE",
                  voice: voiceForSpeaker(line.speaker, lines.map((l) => l.speaker)),
                })
              }
              className="float-right text-muted hover:text-brand"
            >
              🔊
            </button>
            <strong className="text-brand-dark">{line.speaker}:</strong> {line.de}
            <span className="block text-xs text-muted">{line.en}</span>
          </li>
        ))}
      </ul>
      <Button onClick={() => onDone(null)} className="mt-4">
        {t("player.continue")}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scored types
// ---------------------------------------------------------------------------
export function Listening({ payload, onDone }: ActivityProps) {
  const t = useT();
  return (
    <OptionsActivity
      payload={payload}
      onDone={onDone}
      prompt={
        <div>
          <p className="text-sm text-muted">{t("player.listen")} 👂</p>
          <div className="mt-2">
            <SpeakButton text={String(payload.tts ?? "")} />
          </div>
        </div>
      }
    />
  );
}

export function MultipleChoice({ payload, onDone }: ActivityProps) {
  return (
    <OptionsActivity
      payload={payload}
      onDone={onDone}
      prompt={
        <div className="text-lg font-semibold text-ink">
          <TapToTranslate block text={String(payload.prompt ?? "")} />
        </div>
      }
    />
  );
}

export function GapFill({ payload, onDone }: ActivityProps) {
  return (
    <OptionsActivity
      payload={payload}
      onDone={onDone}
      prompt={
        <div>
          <div className="text-lg font-semibold text-ink">
            <TapToTranslate block text={String(payload.sentence ?? "")} />
          </div>
          {typeof payload.hint === "string" && payload.hint && (
            <p className="mt-1 text-xs text-muted">
              💡 <TapToTranslate text={payload.hint} />
            </p>
          )}
        </div>
      }
    />
  );
}

export function TranslatePick({ payload, onDone }: ActivityProps) {
  return (
    <OptionsActivity
      payload={payload}
      onDone={onDone}
      prompt={<p className="text-lg font-semibold text-ink">{String(payload.prompt ?? "")}</p>}
    />
  );
}

/** Normalize for sentence comparison: case + punctuation insensitive. */
export function normalizeSentence(s: string): string {
  return s
    .toLowerCase()
    .replace(/[.,!?;:]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Fisher–Yates shuffle (copy). Used so the sentence-builder tokens aren't
 * already in the correct order — content stores the words in order. If the
 * shuffle happens to return the original order, nudge it so the drill is real. */
export function shuffle<T>(items: T[]): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  if (a.length > 1 && a.every((v, i) => v === items[i])) {
    [a[0], a[1]] = [a[1], a[0]];
  }
  return a;
}

export function SentenceBuilder({ payload, onDone }: ActivityProps) {
  const t = useT();
  const { locale } = useI18n();
  const explanation = pick(payload.explanation as Localised | string | undefined, locale);
  // v2 content: words[] + target. Legacy: tokens[] + answer.
  const answer = String(payload.target ?? payload.answer ?? "");
  const source = (payload.words as string[]) ?? (payload.tokens as string[]) ?? [];
  // Shuffle once per mount so the words aren't already in the right order.
  const tokens = useMemo(() => shuffle(source), [payload]);
  const prompt = String(payload.prompt ?? "");
  const [chosen, setChosen] = useState<number[]>([]);
  const [checked, setChecked] = useState<boolean | null>(null);

  const built = chosen.map((i) => tokens[i]).join(" ");

  if (source.length === 0) {
    // Malformed content — never strand the learner.
    return (
      <div>
        <Button onClick={() => onDone(null)}>{t("player.continue")}</Button>
      </div>
    );
  }

  return (
    <div>
      {prompt && (
        <div className="text-lg font-semibold text-ink">
          <TapToTranslate block text={prompt} />
        </div>
      )}
      <p className="text-sm text-muted">{t("player.buildSentence")}</p>
      <div
        data-testid="built-sentence"
        className="mt-3 min-h-12 rounded-control border border-dashed border-line bg-canvas p-3 text-ink"
      >
        {built || <span className="text-muted">…</span>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {tokens.map((token, i) => (
          <Button
            key={`${token}-${i}`}
            variant="secondary"
            disabled={chosen.includes(i) || checked !== null}
            onClick={() => setChosen((prev) => [...prev, i])}
          >
            {token}
          </Button>
        ))}
      </div>
      {checked === null && (
        <div className="mt-4 flex gap-2">
          <Button
            variant="ghost"
            disabled={chosen.length === 0}
            onClick={() => setChosen([])}
          >
            {t("player.clear")}
          </Button>
          <Button
            disabled={chosen.length !== tokens.length}
            onClick={() => setChecked(normalizeSentence(built) === normalizeSentence(answer))}
          >
            {t("player.check")}
          </Button>
        </div>
      )}
      {checked !== null && (
        <Feedback
          correct={checked}
          answer={answer}
          explanation={explanation}
          onContinue={() => onDone(checked)}
        />
      )}
    </div>
  );
}

export const ARTICLES = ["der", "die", "das"] as const;

export function ArticleSwipe({ payload, onDone }: ActivityProps) {
  const { locale } = useI18n();
  // v2 content: word + answer. Legacy: noun + article.
  const noun = String(payload.word ?? payload.noun ?? "");
  const article = String(payload.answer ?? payload.article ?? "");
  const english = String(payload.english ?? payload.gloss ?? "");
  const explanation = pick(payload.explanation as Localised | string | undefined, locale);
  const [picked, setPicked] = useState<string | null>(null);

  return (
    <div className="text-center">
      <p className="text-sm text-muted">der, die oder das?</p>
      <p className="mt-2 text-3xl font-extrabold text-ink">{noun}</p>
      {english && <p className="text-sm text-muted">{english}</p>}
      <div className="mt-4 flex flex-wrap justify-center gap-3">
        {ARTICLES.map((a) => (
          <Button
            key={a}
            variant={
              picked === null ? "secondary" : a === article ? "primary" : a === picked ? "danger" : "secondary"
            }
            disabled={picked !== null}
            onClick={() => setPicked(a)}
          >
            {a}
          </Button>
        ))}
      </div>
      {picked !== null && (
        <Feedback
          correct={picked === article}
          answer={`${article} ${noun}`}
          explanation={explanation}
          onContinue={() => onDone(picked === article)}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// The lesson spine (Learn rebuild)
// ---------------------------------------------------------------------------

/** A declension / conjugation table shown inside a lesson.
 *
 * The payload carries only `table_slug`; the server resolves it to the live
 * row before the lesson is sent, so one corrected table fixes every lesson
 * that shows it. If the table is missing the activity self-skips rather
 * than blocking the learner.
 */
function ReferenceTable({ payload, onDone }: ActivityProps) {
  const t = useT();
  const table = payload.table as GrammarTable | null | undefined;
  const intro = payload.intro as string | undefined;

  if (!table) {
    return (
      <div>
        <p className="text-sm text-muted">{t("player.tableMissing")}</p>
        <Button onClick={() => onDone(null)} className="mt-3">
          {t("player.continue")}
        </Button>
      </div>
    );
  }
  return (
    <div>
      {intro && (
        <div className="mb-3 text-ink">
          <TapToTranslate block text={intro} />
        </div>
      )}
      <GrammarTableView table={table} />
      <Button onClick={() => onDone(null)} className="mt-4">
        {t("player.continue")}
      </Button>
    </div>
  );
}

/** The memory hook for this lesson's rule. */
function Mnemonic({ payload, onDone }: ActivityProps) {
  const t = useT();
  return (
    <div>
      <div className="rounded-card border border-brand/30 bg-brand-soft p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-dark">
          {t("player.mnemonic")}
        </p>
        <div className="mt-2 text-lg font-bold text-ink">
          <TapToTranslate block text={String(payload.hook ?? "")} />
        </div>
        {payload.explanation != null && (
          <p className="mt-2 text-sm text-ink">{String(payload.explanation)}</p>
        )}
      </div>
      <Button onClick={() => onDone(null)} className="mt-4">
        {t("player.continue")}
      </Button>
    </div>
  );
}

/** Write a short text, then compare against a model answer.
 *
 * Deliberately self-checked rather than AI-graded: the Learn tab teaches
 * and the Practice tab tests, where full exam tasks already go to the AI
 * coaches. Grading every lesson activity would also bill an institute for
 * every keystroke of practice.
 */
function WritingTask({ payload, onDone }: ActivityProps) {
  const t = useT();
  const [text, setText] = useState("");
  const [revealed, setRevealed] = useState(false);
  const points = (payload.points as string[] | undefined) ?? [];
  const minWords = Number(payload.min_words ?? 0);
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  return (
    <div>
      <div className="text-ink">
        <TapToTranslate block text={String(payload.instruction ?? "")} />
      </div>
      {points.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-sm text-muted">
          {points.map((p) => (
            <li key={p}>
              <TapToTranslate text={p} />
            </li>
          ))}
        </ul>
      )}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label={t("player.yourText")}
        rows={5}
        className="mt-3 w-full rounded-control border border-line bg-surface p-3 text-ink"
      />
      <p className="mt-1 text-xs text-muted">
        {t("player.wordCount", { count: words })}
        {minWords > 0 ? ` / ${minWords}` : ""}
      </p>

      {!revealed ? (
        <Button onClick={() => setRevealed(true)} className="mt-3" disabled={words === 0}>
          {t("player.compare")}
        </Button>
      ) : (
        <div className="mt-4 rounded-card border border-line bg-surface p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
            {t("player.modelAnswer")}
          </p>
          <div className="mt-2 text-ink">
            <TapToTranslate block text={String(payload.model_answer ?? "")} />
          </div>
          {Array.isArray(payload.checklist) && (
            <ul className="mt-3 list-disc pl-5 text-sm text-muted">
              {(payload.checklist as string[]).map((c) => (
                <li key={c}>
                  <TapToTranslate text={c} />
                </li>
              ))}
            </ul>
          )}
          <Button onClick={() => onDone(null)} className="mt-3">
            {t("player.continue")}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Say it aloud, then hear the model sentence. */
function SpeakingTask({ payload, onDone }: ActivityProps) {
  const t = useT();
  const [revealed, setRevealed] = useState(false);
  const prompts = (payload.prompts as string[] | undefined) ?? [];
  const model = String(payload.model_answer ?? "");

  return (
    <div>
      <div className="text-ink">
        <TapToTranslate block text={String(payload.instruction ?? "")} />
      </div>
      {prompts.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-sm text-muted">
          {prompts.map((p) => (
            <li key={p}>
              <TapToTranslate text={p} />
            </li>
          ))}
        </ul>
      )}
      {!revealed ? (
        <Button onClick={() => setRevealed(true)} className="mt-3">
          {t("player.saidIt")}
        </Button>
      ) : (
        <div className="mt-4 rounded-card border border-line bg-surface p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
            {t("player.modelAnswer")}
          </p>
          <div className="mt-2 text-lg text-ink">
            <TapToTranslate block text={model} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <SpeakButton text={model} />
            <Button onClick={() => onDone(null)}>{t("player.continue")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------
const RENDERERS: Record<string, (props: ActivityProps) => JSX.Element> = {
  grammar_tip: GrammarTip,
  example_sentence: ExampleSentence,
  dialogue: Dialogue,
  listening: Listening,
  multiple_choice: MultipleChoice,
  gap_fill: GapFill,
  translate_pick: TranslatePick,
  sentence_builder: SentenceBuilder,
  article_swipe: ArticleSwipe,
  reference_table: ReferenceTable,
  mnemonic: Mnemonic,
  writing_task: WritingTask,
  speaking_task: SpeakingTask,
  // The teachable-lesson blocks (curriculum §3), incl. bilingual rendering.
  ...LEARN_BLOCK_RENDERERS,
};

export function ActivityRenderer({
  type,
  payload,
  onDone,
}: ActivityProps & { type: string }) {
  const t = useT();
  const Renderer = RENDERERS[type];
  if (!Renderer) {
    // Unknown type (content newer than this build) — skip, don't crash,
    // and don't count it against the score.
    return (
      <div>
        <p className="text-sm text-muted">({type})</p>
        <Button onClick={() => onDone(null)} className="mt-3">
          {t("player.continue")}
        </Button>
      </div>
    );
  }
  return <Renderer payload={payload} onDone={onDone} />;
}
